import * as THREE from "three";
import { MATS } from "../materials.js";
import { makeRng } from "../builder.js";
import { burrBush } from "../greenspace.js";
import * as B from "./backspec.js";

/**
 * The pale blue-grey shed at the end of the back garden, with the mulched
 * border it stands against and the clutter beside it.
 *
 * The shed roof is the highest standing surface in the garden, and the west
 * side of the shed is an honest ladder to it.  b.roof()'s collider is a flat
 * box sitting ON the eave line, `max(rise * 0.55, 0.4)` tall, so the surface
 * the cat actually stands on is eave + 0.4675 = 2.5675, NOT the eave itself.
 * Every rung below is sized against that number (cat jumps 0.95):
 *
 *   grade 0.00 -> timber offcut stack 0.46   (+0.46)
 *              -> water butt lid      0.92   (+0.46)
 *              -> log store top       1.72   (+0.80)
 *              -> roof collider top   2.5675 (+0.8475)
 *
 * The largest rise, 0.8475, matches the porch's grade->pier-cap hop (0.82),
 * which is the established "big but makeable" jump in this level.
 */

const S = B.SHED;
const MINX = S.x - S.width / 2;            // -2.425
const MAXX = S.x + S.width / 2;            //  0.625
const MINZ = S.z - S.depth / 2;            // -29.575  (rear, toward fence)
const FRONT = S.z + S.depth / 2;           // -27.125  (faces the lawn/house)
const RISE = S.ridge - S.eave;             //  0.85

const DOOR_X = -1.35;                      // slightly left of the wall centre
const WIN_X = -0.05;                       // window right of the door
const SILL = 1.10;                         // window sill height
const WIN_W = 0.72, WIN_H = 0.66;

// Roof overhang: rakes 0.28 past each gable, eaves 0.25 past the walls.  The
// prism keeps the spec's eave (2.10) and ridge (2.95) exactly, so the pitch
// flattens a touch rather than the eave line dropping.
const ROOF_W = S.width + 0.56;             // 3.61
const ROOF_D = S.depth + 0.50;             // 2.95
const ROOF_TOP = S.eave + Math.max(RISE * 0.55, 0.4);   // 2.5675, see header

// Ladder rung tops (see header arithmetic).
const RUNG1 = 0.46;                        // timber offcut stack
const RUNG2 = 0.92;                        // water butt lid
const RUNG3 = 1.72;                        // log store top

export function build(b) {
  const rng = makeRng(0x5eed58ed);
  gravelPad(b);
  shedBody(b);
  roofAssembly(b);
  doorAssembly(b);
  windowAssembly(b);
  flowerBox(b, WIN_X, FRONT);
  backBorder(b, rng);
  burrCorner(b);
  climbLadder(b);
  oddsAndEnds(b, rng);
  planters(b, rng);
}

// ---------------------------------------------------------------------------
function gravelPad(b) {
  // Crushed-stone pad, a pale band round the base.  Rear edge pulled to
  // -29.975 so it stops just short of the fence line at -30.
  const w = S.width + S.padOversize * 2;
  const d = S.depth + S.padOversize * 2 - 0.10;
  b.slab(S.x, 0.014, S.z + 0.05, w, d, MATS.stone,
         { thickness: 0.045, tile: 0.33 });
}

function shedBody(b) {
  // One solid block: single collider, no interior for the cat to glitch into.
  b.block(S.x, 0, S.z, S.width, S.eave, S.depth, MATS.shedBlue, { tile: 0.7 });

  // Shadow-side skins.  Sun sits at (+x, +z), so west and rear read darker.
  // 1 mm sunk into the body so the hidden face cannot z-fight.
  b.block(MINX - 0.010, 0.015, S.z, 0.022, S.eave - 0.03, S.depth - 0.006,
          MATS.shedShadow, { solid: false, tile: 0.7 });
  b.block(S.x, 0.015, MINZ - 0.010, S.width - 0.006, S.eave - 0.03, 0.022,
          MATS.shedShadow, { solid: false, tile: 0.7 });

  // Board-and-batten: slim raised battens, painted the wall colour.  The
  // front row skips the door surround and window frame spans.
  battens(b, "x", FRONT + 0.008, MINX + 0.12, MAXX - 0.12, 0.31,
          MATS.shedBlue, [[-1.86, -0.84], [-0.49, 0.39]]);
  battens(b, "x", MINZ - 0.027, MINX + 0.12, MAXX - 0.12, 0.40,
          MATS.shedShadow, []);
  battens(b, "z", MAXX + 0.008, MINZ + 0.14, FRONT - 0.14, 0.31,
          MATS.shedBlue, []);
  battens(b, "z", MINX - 0.027, MINZ + 0.14, FRONT - 0.14, 0.31,
          MATS.shedShadow, []);

  // White corner boards, square posts straddling each corner.
  for (const [cx, cz, m] of [
    [MINX + 0.01, FRONT - 0.01, MATS.trim], [MAXX - 0.01, FRONT - 0.01, MATS.trim],
    [MINX + 0.01, MINZ + 0.01, MATS.trimShade], [MAXX - 0.01, MINZ + 0.01, MATS.trimShade],
  ]) {
    b.block(cx, 0.03, cz, 0.075, S.eave - 0.08, 0.075, m, { solid: false });
  }
}

/** A row of battens on one face; `skips` are [min,max] spans to leave open. */
function battens(b, along, faceAt, from, to, step, mat, skips) {
  for (let p = from; p <= to + 1e-6; p += step) {
    if (skips.some(([a, z]) => p > a && p < z)) continue;
    if (along === "x") {
      b.block(p, 0.04, faceAt, 0.04, S.eave - 0.12, 0.04, mat, { solid: false });
    } else {
      b.block(faceAt, 0.04, p, 0.04, S.eave - 0.12, 0.04, mat, { solid: false });
    }
  }
}

// ---------------------------------------------------------------------------
function roofAssembly(b) {
  // Gable ridged across the front (along x).  Default collider: flat box on
  // the eave line, top at ROOF_TOP -- the roof's standing surface.
  b.roof(S.x, S.eave, S.z, ROOF_W, ROOF_D, RISE, MATS.shingle, { ridge: "x" });

  // Siding gable ends: thin prisms flush with the roof ends, fractionally
  // oversized so their slopes sit ~2 cm proud of the shingles (no coplanar
  // fighting) and read as the painted gable, not shingle end-grain.
  const capD = ROOF_D + 0.04, capH = RISE + 0.02;
  b.roof(MINX - 0.235, S.eave - 0.01, S.z, 0.09, capD, capH,
         MATS.shedShadow, { ridge: "x", solid: false });
  b.roof(MAXX + 0.235, S.eave - 0.01, S.z, 0.09, capD, capH,
         MATS.shedBlue, { ridge: "x", solid: false });

  // White barge boards down the four rakes.
  const half = capD / 2, riseC = capH + 0.01;
  const ang = Math.atan2(riseC, half);
  const len = Math.hypot(half, riseC);
  for (const ex of [MINX - 0.28, MAXX + 0.28]) {
    for (const side of [1, -1]) {
      const g = new THREE.BoxGeometry(0.055, 0.14, len);
      g.rotateX(side * ang);
      g.translate(ex, S.eave + riseC / 2 - 0.035, S.z + side * half / 2);
      b.add(g, MATS.trim, {});
    }
  }

  // White fascia under each eave edge (front lit, rear shaded).
  b.block(S.x, S.eave - 0.11, FRONT + 0.225, ROOF_W, 0.12, 0.05,
          MATS.trim, { solid: false });
  b.block(S.x, S.eave - 0.11, MINZ - 0.225, ROOF_W, 0.12, 0.05,
          MATS.trimShade, { solid: false });

  // Ridge cap, hiding the seam where the gable caps peak proud of the roof.
  b.block(S.x, S.ridge - 0.045, S.z, ROOF_W + 0.02, 0.075, 0.13,
          MATS.shingleAlt, { solid: false });
}

// ---------------------------------------------------------------------------
function doorAssembly(b) {
  const zFace = FRONT;
  // Leaf sits 2 mm into the wall so only its front face shows.
  b.block(DOOR_X, 0.03, zFace - 0.013, 0.82, 1.88, 0.030, MATS.trim,
          { solid: false });
  // Surround: jambs and head, proud of the leaf.
  for (const jx of [DOOR_X - 0.45, DOOR_X + 0.45]) {
    b.block(jx, 0.02, zFace + 0.005, 0.08, 1.99, 0.05, MATS.trim, { solid: false });
  }
  b.block(DOOR_X, 1.93, zFace + 0.005, 0.98, 0.08, 0.05, MATS.trim, { solid: false });

  // Window light in the upper half: shadow reveal, then the glass.
  b.block(DOOR_X, 1.12, zFace + 0.006, 0.46, 0.54, 0.010, MATS.trimShade,
          { solid: false });
  b.block(DOOR_X, 1.16, zFace + 0.012, 0.38, 0.46, 0.010, MATS.glass,
          { solid: false });
  // Raised panel on the lower half.
  b.block(DOOR_X, 0.22, zFace + 0.006, 0.50, 0.62, 0.010, MATS.trimShade,
          { solid: false });
  b.block(DOOR_X, 0.28, zFace + 0.012, 0.38, 0.50, 0.010, MATS.trim,
          { solid: false });
  // Dark handle on the right stile.
  b.block(DOOR_X + 0.32, 0.92, zFace + 0.020, 0.035, 0.16, 0.030,
          MATS.metalDark, { solid: false });

  // Concrete step at the threshold: 0.075 high, under the 0.23 step-up.
  b.block(DOOR_X, 0, zFace + 0.135, 0.95, 0.075, 0.26, MATS.concrete);
}

function windowAssembly(b) {
  const zFace = FRONT;
  b.block(WIN_X, SILL + 0.03, zFace - 0.013, WIN_W - 0.06, WIN_H - 0.06, 0.030,
          MATS.glass, { solid: false });
  // Frame: jambs, head, and a deeper sill ledge.
  for (const jx of [WIN_X - WIN_W / 2, WIN_X + WIN_W / 2]) {
    b.block(jx, SILL, zFace + 0.005, 0.06, WIN_H, 0.05, MATS.trim, { solid: false });
  }
  b.block(WIN_X, SILL + WIN_H - 0.03, zFace + 0.005, WIN_W + 0.06, 0.06, 0.05,
          MATS.trim, { solid: false });
  b.block(WIN_X, SILL - 0.05, zFace + 0.015, WIN_W + 0.10, 0.055, 0.09,
          MATS.trim, { solid: false });
  // Muntins: one vertical, one horizontal -- four panes.
  b.block(WIN_X, SILL + 0.03, zFace + 0.003, 0.028, WIN_H - 0.06, 0.026,
          MATS.trim, { solid: false });
  b.block(WIN_X, SILL + WIN_H / 2 - 0.014, zFace + 0.003, WIN_W - 0.06, 0.028,
          0.026, MATS.trim, { solid: false });
}

function flowerBox(b, x, zFace) {
  const rng = makeRng(0xf70e78);
  // The box itself is solid (top 1.04 -- above a grade jump, so unreachable
  // and never a perch that skips the ladder).
  b.block(x, 0.87, zFace + 0.085, 0.80, 0.17, 0.17, MATS.trim);
  b.block(x, 1.035, zFace + 0.085, 0.72, 0.015, 0.13, MATS.soil, { solid: false });

  // Planting spilling out: foliage mounded over the rim, flowers on top,
  // a few trailers dropping past the front edge.  All non-solid.
  for (let i = 0; i < 9; i++) {
    const px = x - 0.33 + (i / 8) * 0.66;
    b.sphere(px, 1.00 + rng() * 0.05, zFace + 0.075 + (rng() - 0.5) * 0.07,
             0.055 + rng() * 0.030, rng() < 0.35 ? MATS.leafDark : MATS.leaf,
             { segments: 6, rings: 4, solid: false });
  }
  for (let i = 0; i < 6; i++) {
    b.sphere(x - 0.30 + (i / 5) * 0.60, 1.075 + rng() * 0.045,
             zFace + 0.08 + (rng() - 0.5) * 0.05, 0.030 + rng() * 0.016,
             rng() < 0.6 ? MATS.flowerWarm : MATS.flowerPale,
             { segments: 6, rings: 4, solid: false });
  }
  for (let i = 0; i < 3; i++) {
    b.sphere(x - 0.24 + i * 0.24, 0.90 - rng() * 0.06, zFace + 0.165,
             0.038 + rng() * 0.015, MATS.leaf,
             { segments: 5, rings: 4, solid: false });
  }
}

// ---------------------------------------------------------------------------
function backBorder(b, rng) {
  // Mulched border in front of the conifer screen, the full width of the LOT.
  //
  // It used to be a 14.6 m slab centred on 0, which is wider than the 13.5 m
  // lot it sits in: it ran to +/-7.3 and the boundary fences are at +/-6.75,
  // so a third of a metre of mulch pushed through the boards into lots 0 and
  // 2 on both sides.  That is the same rot backspec.js documents -- numbers
  // typed against a lot spacing that later tightened -- so it is derived now
  // and cannot drift again.
  const midZ = (B.BACK_BED.minZ + B.BACK_BED.maxZ) / 2;
  const depth = B.BACK_BED.maxZ - B.BACK_BED.minZ;
  const bedMinX = B.LOT_MIN_X + B.BED_INSET, bedMaxX = B.LOT_MAX_X - B.BED_INSET;
  b.slab((bedMinX + bedMaxX) / 2, 0.008, midZ, bedMaxX - bedMinX, depth,
         MATS.mulch, { thickness: 0.03, tile: 0.9 });

  // Timber edging along the bed's front, broken where the shed pad crosses.
  // 0.10 high -- under the 0.23 step, so it never obstructs anything.  Both
  // runs ran past the boundary too (-7.25 and +7.25); they stop on the mulch
  // now, which is what they are edging.
  const ez = B.BACK_BED.maxZ - 0.02;
  const bar = (x0, x1) =>
    b.bar((x0 + x1) / 2, 0, ez, 0.05, x1 - x0, "x", MATS.timber);
  bar(bedMinX, -2.95);
  bar(1.115, bedMaxX);

  // Low shrubs.  Skipped over the shed/pad/ladder zone, and clearOf() keeps
  // them out of the rear gate circle; non-solid regardless.
  // Scattered across the bed, not across a 14 m span in a 13.5 m lot: the old
  // range reached +/-7.0 and only the seeded RNG kept a crown off the boards.
  // insideBoundary clamps each shrub for the reach of its own blob cluster.
  let placed = 0;
  for (let i = 0; i < 40 && placed < 8; i++) {
    const r = 0.26 + rng() * 0.17;
    // Clamp FIRST, then test: the shed zone and the keep-clear circles have to
    // be judged where the shrub actually ends up, not where it was rolled.
    // The offset blob below sits 0.7 r to +x and carries 0.55 r of its own, so
    // the cluster reaches 1.25 r past its centre -- that is what has to clear
    // the boards, not the centre.
    const x = B.insideBoundary(bedMinX + rng() * (bedMaxX - bedMinX), r * 1.25);
    const z = -29.35 + rng() * 1.05;
    if (x > -3.95 && x < 1.40) continue;          // shed, pad, and ladder zone
    if (!B.clearOf(x, z, 0.5)) continue;          // keep-clear routes stay open
    // Keep the scatter off the burr bush (burrCorner below): a live green
    // dome grown into the one plant that snags would blur the exact
    // silhouette the player is being taught to trust.
    if (Math.hypot(x - BURR_SPOT.x, z - BURR_SPOT.z) < 1.1) continue;
    const m = [MATS.boxwood, MATS.hedge, MATS.leafDark][(rng() * 3) | 0];
    b.sphere(x, 0.02, z, r, m,
             { squash: 0.68 + rng() * 0.14, segments: 7, rings: 5, solid: false });
    if (rng() < 0.4) {
      b.sphere(x + r * 0.7, 0.02, z + (rng() - 0.5) * r, r * 0.55, MATS.boxwood,
               { squash: 0.7, segments: 6, rings: 4, solid: false });
    }
    placed++;
  }
}

// ---------------------------------------------------------------------------
/**
 * One burr bush in the back border's neglected west corner -- greenspace.js's
 * burrBush, because the burred plant is ONE plant everywhere by design (its
 * header carries the fairness argument), and this garden does not get a
 * variant.
 *
 * The spot is argued, not rolled, which is why it is not one of backBorder's
 * scattered shrubs promoted at random: (-5.6, -28.45) sits in the strip west
 * of the shed that nothing routes through -- 7.0 m from the "nose around the
 * shed" disc (1.36, -28.03, r 2.0) and its whole approach, 6.2 m from the
 * rear-fence hop the brains cross at (0.4, -30), 2.6 m from the westmost
 * planter, and outside the ladder zone (x > -3.95) with the whole timber
 * stack between it and the roof route.  The z is the tight number: the
 * fence-walk zone is a 0.9 m capsule along z = -30, and burrs.test.mjs
 * holds every bush's whole snag disc outside every zone's whole radius --
 * 1.45 m -- so the bush stands at the bed's front edge, 1.55 m off the
 * fence line, where a cat that loses the walk falls to bare mulch and
 * never into burrs.  A brain can only find it by
 * wandering; a player finds it exactly where a garden's forgotten corner
 * would let a weed go to seed, two strides from the shed they were sent to
 * nose around -- close enough to teach, far enough to never ambush the
 * errand.  It stands against the dark conifer screen, which is the best
 * backdrop its rusty ochre has anywhere in the level.
 *
 * Its own rng stream so the bush's ornament cannot reshuffle backBorder's
 * scatter, which was placed and photographed before burrs existed.
 */
const BURR_SPOT = { x: -5.6, z: -28.45 };

function burrCorner(b) {
  burrBush(b, makeRng(0xb1122ee), BURR_SPOT.x, BURR_SPOT.z, "shed-corner");
}

// ---------------------------------------------------------------------------
/**
 * The ladder to the roof, on the shaded west side so the photographed front
 * elevation stays clean.  Heights per the header comment; horizontal hops are
 * 0.76-0.82 m centre-to-centre, and the log store top overlaps under the
 * roof's west overhang (roof collider starts at 2.10; the cat at 1.72 has
 * 0.38 of headroom there, enough for its 0.24 box).
 */
function climbLadder(b) {
  // Rung 1: criss-cross stack of timber offcuts, top at 0.46.
  const sx = -3.38, sz = -27.52;
  for (const dz of [-0.14, 0.14]) b.block(sx, 0, sz + dz, 0.78, 0.15, 0.16, MATS.timber);
  for (const dx of [-0.22, 0.22]) b.block(sx + dx, 0.15, sz, 0.16, 0.15, 0.62, MATS.timber);
  for (const dz of [-0.12, 0.12]) b.block(sx, 0.30, sz + dz, 0.72, RUNG1 - 0.30, 0.17, MATS.timber);
  b.mark("shed.timberStack", sx, RUNG1, sz);

  // Rung 2: green plastic water butt, lid at 0.92.
  const wx = -3.05, wz = -28.20;
  b.post(wx, 0, wz, 0.29, RUNG2 - 0.06, MATS.plasticGreen, { segments: 12 });
  b.post(wx, RUNG2 - 0.06, wz, 0.31, 0.06, MATS.plasticGreen,
         { segments: 12, topRadius: 0.27 });
  b.block(wx + 0.27, 0.16, wz + 0.10, 0.05, 0.08, 0.05, MATS.metalDark,
          { solid: false });                       // tap
  b.mark("shed.waterButt", wx, RUNG2, wz);

  // Rung 3: slim log store against the wall, flat timber top at 1.72.
  // One hand-placed collider for the whole thing; the visual pieces are
  // non-solid so the cat interacts with a clean box.
  const lx = -2.72, lz = -28.95;
  b.collider(lx, 0, lz, 0.56, RUNG3, 0.52);
  b.block(lx, 0, lz, 0.56, 0.10, 0.50, MATS.timber, { solid: false });
  for (const dx of [-0.255, 0.255]) {
    for (const dz of [-0.22, 0.22]) {
      b.block(lx + dx, 0.10, lz + dz, 0.05, RUNG3 - 0.18, 0.05, MATS.timber,
              { solid: false });
    }
  }
  b.block(lx, 0.86, lz, 0.54, 0.045, 0.48, MATS.timber, { solid: false });
  for (const [y0, y1] of [[0.10, 0.86], [0.905, RUNG3 - 0.08]]) {
    for (let y = y0; y + 0.10 <= y1 + 1e-6; y += 0.105) {
      for (const dz of [-0.115, 0.115]) {
        b.bar(lx, y, lz + dz, 0.052, 0.46, "x",
              (y * 7 + dz) % 0.2 > 0.1 ? MATS.bark : MATS.barkDark,
              { solid: false, segments: 7 });
      }
    }
  }
  b.block(lx, RUNG3 - 0.08, lz, 0.60, 0.08, 0.54, MATS.timber, { solid: false });
  b.mark("shed.logStore", lx, RUNG3, lz);

  // Final hop: RUNG3 (1.72) -> roof collider top (2.5675), rise 0.8475.
  b.mark("shed.roof", S.x, ROOF_TOP, S.z);
}

// ---------------------------------------------------------------------------
function oddsAndEnds(b, rng) {
  // Terracotta pots by the front-west corner, on the gravel.  No terracotta
  // in MATS; brick is the closest clay read.
  pot(b, -2.62, -26.96, 0.13, 0.30, true, rng);
  pot(b, -2.32, -26.82, 0.095, 0.22, false, rng);

  // Spade leaning on the east wall, all non-solid so the gate-side lane
  // past the shed stays frictionless.
  const shaft = new THREE.CylinderGeometry(0.021, 0.021, 1.12, 7);
  shaft.rotateZ(0.12);                       // top leans in to touch the wall
  shaft.translate(0.7325, 0.58, -27.85);
  b.add(shaft, MATS.timber, { tile: 0 });
  b.block(0.80, 0.01, -27.85, 0.17, 0.30, 0.035, MATS.metalDark, { solid: false });
  b.block(0.665, 1.12, -27.85, 0.15, 0.05, 0.05, MATS.timber, { solid: false });
}

function pot(b, x, z, r, h, planted, rng) {
  b.post(x, 0, z, r * 0.72, h - 0.045, MATS.brick, { segments: 10, topRadius: r * 0.95 });
  b.post(x, h - 0.045, z, r, 0.045, MATS.brick, { segments: 10 });
  b.post(x, h - 0.02, z, r * 0.82, 0.012, MATS.soil, { segments: 8, solid: false });
  if (planted) {
    b.sphere(x, h - 0.02, z, r * 0.95, MATS.leaf,
             { squash: 0.85, segments: 6, rings: 4, solid: false });
    for (let i = 0; i < 3; i++) {
      const a = rng() * Math.PI * 2;
      b.sphere(x + Math.cos(a) * r * 0.5, h + r * 0.55 + rng() * 0.03,
               z + Math.sin(a) * r * 0.5, 0.030 + rng() * 0.012,
               MATS.flowerWarm, { segments: 5, rings: 4, solid: false });
    }
  }
}

// ---------------------------------------------------------------------------
/**
 * A pair of white rectangular planters flanking the shed.
 *
 * Sized and placed off the shed's own footprint so they stay square to it and
 * move with it.  The box is solid at 0.44 -- a jump a cat makes easily from
 * grade, and a useful first rung on this side of the shed; the soil and the
 * planting on top are not, so nothing invisible snags.
 */
function planters(b, rng) {
  const W = 1.15, D = 0.48, H = 0.44;
  const halfShed = B.SHED.width / 2;
  for (const side of [-1, 1]) {
    const x = B.SHED.x + side * (halfShed + 0.16 + W / 2);
    const z = B.SHED.z + 0.32;

    // carcass: four rails plus a base, so it reads as a box rather than a slab
    b.block(x, 0, z, W, H, D, MATS.trim);
    for (const [ox, oz, ww, dd] of [
      [0, -D / 2 + 0.03, W, 0.06], [0, D / 2 - 0.03, W, 0.06],
      [-W / 2 + 0.03, 0, 0.06, D], [W / 2 - 0.03, 0, 0.06, D],
    ]) {
      b.block(x + ox, H - 0.07, z + oz, ww, 0.09, dd, MATS.trimShade,
        { solid: false });                       // top rail, proud of the sides
    }
    b.slab(x, H - 0.045, z, W - 0.14, D - 0.14, MATS.soil, { thickness: 0.03 });

    // planting: mounded foliage with a few flowers, none of it solid
    for (let i = 0; i < 5; i++) {
      const t = (i + 0.5) / 5;
      b.sphere(x - W / 2 + t * W, H - 0.03, z + (rng() - 0.5) * 0.16,
        0.15 + rng() * 0.07, i % 2 ? MATS.boxwood : MATS.leafDark,
        { squash: 0.8, segments: 6, rings: 4, solid: false });
    }
    for (let i = 0; i < 4; i++) {
      b.sphere(x - W / 2 + 0.18 + rng() * (W - 0.36), H + 0.06,
        z + (rng() - 0.5) * 0.2, 0.05 + rng() * 0.03,
        rng() < 0.5 ? MATS.flowerWarm : MATS.flowerPale,
        { segments: 5, rings: 4, solid: false });
    }
    b.mark(`shed.planter${side < 0 ? "W" : "E"}`, x, H, z);
  }
}
